import React, { useState } from 'react';
import { saveAs } from 'file-saver';
import apiService from '../../services/ApiService';
import TailwindModal from '../Tailwind/TailwindModal';
import TailwindButton from '../Tailwind/TailwindButton';

interface Props {
    scenarioId: number;
    scenarioName: string;
    disabled?: boolean;
}

interface Mismatch {
    kpi: string;
    sheet: string;
    cell: string;
    excel: number | string | null;
    backend: number | string | null;
}

interface PendingExport {
    blob: Blob;
    filename: string;
    status: 'pass' | 'fail' | 'skipped';
    mismatches: Mismatch[];
    checked: number;
}

export default function ValuationExport({ scenarioId, scenarioName, disabled }: Props) {
    const [exporting, setExporting] = useState(false);
    const [pending, setPending] = useState<PendingExport | null>(null);

    const fetchAndPrepare = async () => {
        setExporting(true);
        try {
            const response = await apiService.exportValuationKpis(scenarioId);
            if (!response || !response.ok) {
                console.error('Valuation export failed:', response?.status, response?.statusText);
                return;
            }

            const status = (response.headers.get('X-Kpi-Validation-Runtime') as PendingExport['status']) || 'skipped';
            const checked = parseInt(response.headers.get('X-Kpi-Validation-Checked') || '0', 10);
            const mismatchesRaw = response.headers.get('X-Kpi-Validation-Detail');
            let mismatches: Mismatch[] = [];
            try {
                mismatches = mismatchesRaw ? JSON.parse(mismatchesRaw) : [];
            } catch {
                mismatches = [];
            }

            const sanityIssueCount = parseInt(response.headers.get('X-Sanity-Issue-Count') || '0', 10);
            if (sanityIssueCount > 0) {
                console.warn(`[ValuationExport] Backend sanity check reported ${sanityIssueCount} issue(s)`);
            }

            const blob = await response.blob();
            const fallback = `${scenarioName.replace(/[^a-zA-Z0-9]/g, '_')}_Valuation_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
            const filename = parseFilenameFromContentDisposition(response.headers.get('Content-Disposition')) || fallback;

            if (status === 'fail') {
                setPending({ blob, filename, status, mismatches, checked });
            } else {
                if (status === 'skipped') {
                    console.info('[ValuationExport] KPI runtime validation skipped (LibreOffice unavailable).');
                } else {
                    console.info(`[ValuationExport] KPI runtime validation passed (${checked} KPIs checked).`);
                }
                saveAs(blob, filename);
            }
        } catch (err) {
            console.error('Valuation export failed:', err);
        } finally {
            setExporting(false);
        }
    };

    const handleConfirmDownload = () => {
        if (pending) {
            saveAs(pending.blob, pending.filename);
            setPending(null);
        }
    };

    const handleCancelDownload = () => {
        setPending(null);
    };

    return (
        <>
            <button
                onClick={fetchAndPrepare}
                disabled={exporting || !!disabled}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
                title="Export valuation KPI workbook (with native Excel formulas)"
            >
                {exporting ? (
                    <>
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                        Exporting...
                    </>
                ) : (
                    <>
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                    </>
                )}
            </button>

            <TailwindModal
                isOpen={pending !== null}
                onClose={handleCancelDownload}
                title="Excel values do not match"
                size="2xl"
                footer={
                    <div className="flex justify-end gap-3 w-full">
                        <TailwindButton variant="ghost" icon="close" size="sm" onClick={handleCancelDownload}>
                            Cancel
                        </TailwindButton>
                        <TailwindButton variant="primary" icon="download" size="sm" onClick={handleConfirmDownload}>
                            Download anyway
                        </TailwindButton>
                    </div>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-slate-700">
                        The recalculated workbook has{' '}
                        <strong>
                            {pending?.mismatches.length} mismatch{pending?.mismatches.length === 1 ? '' : 'es'}
                        </strong>{' '}
                        compared to the values stored on the server.
                        The downloaded file may show numbers that differ from what's displayed in the app.
                    </p>

                    {pending && pending.mismatches.length > 0 && (
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-medium text-slate-600">KPI</th>
                                        <th className="text-left px-4 py-2 font-medium text-slate-600">Cell</th>
                                        <th className="text-right px-4 py-2 font-medium text-slate-600">Excel value</th>
                                        <th className="text-right px-4 py-2 font-medium text-slate-600">Server value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pending.mismatches.map((m, i) => (
                                        <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                            <td className="px-4 py-2 font-mono text-slate-900">{m.kpi}</td>
                                            <td className="px-4 py-2 text-slate-600">{m.sheet}!{m.cell}</td>
                                            <td className="px-4 py-2 text-right font-mono text-amber-700">
                                                {formatVal(m.excel)}
                                            </td>
                                            <td className="px-4 py-2 text-right font-mono text-slate-700">
                                                {formatVal(m.backend)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <p className="text-xs text-slate-500">
                        Tip: download the file to inspect the formulas in Excel, then re-run the valuation compute
                        to see if the discrepancy persists. If it does, please report it.
                    </p>
                </div>
            </TailwindModal>
        </>
    );
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : null;
}

function formatVal(v: number | string | null): string {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') {
        if (Math.abs(v) < 1) return v.toFixed(4);
        if (Math.abs(v) < 1000) return v.toFixed(2);
        return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    return String(v);
}
